import type { MailTestSendStatusValue } from './mailDomainCheck';

/**
 * The Set up company mail stepper on Settings › Email › Mailboxes (piece 32;
 * decision 1771): four steps, each a done test read from rows the plan already
 * names, and whether the step can be reached yet. 🔒 Nothing here is stored.
 * The records and sending steps read the stored check and test send (decision 2541).
 */
export const MAIL_SETUP_STEPS_PATH = '/mail/settings/setup-steps';

export const MAIL_SETUP_STEPS = ['domain', 'host', 'sharedMailboxes', 'sending'] as const;
export type MailSetupStep = (typeof MAIL_SETUP_STEPS)[number];

/** The three verdicts of one stored domain check, as `AuthResult` members (decision 2541). */
export interface MailDomainVerdicts {
  spf: string;
  dkim: string;
  dmarc: string;
}

/** What the server reads; every field is a saved row's fact and none is stored for the stepper. */
export interface MailSetupStepsView {
  /** The Organization's current email domain, or null while only `localhost` is registered. */
  domain: string | null;
  /** The verdicts of the newest Check Records of that domain; null while none is stored. */
  verdicts: MailDomainVerdicts | null;
  /** When that check was made; null while none is stored. */
  checkedAt: string | null;
  /** Mailboxes whose Inbox folder the host has answered for (an Inbox with a stored UIDVALIDITY). */
  hostReached: number;
  /** Department mailboxes that are not distribution lists. */
  departmentMailboxes: number;
  /** Module mailboxes. */
  moduleMailboxes: number;
  /** The newest Send Test Message: the host's answer and when; null while none was sent. */
  testSend: { status: MailTestSendStatusValue; sentAt: string } | null;
}

export interface MailSetupStepFacts {
  done: boolean;
  /** Whether what the step needs is done; a step that cannot be reached draws as later. */
  reachable: boolean;
}

/**
 * 🔒 The overall reading of one domain's newest check (decision 13291, amending 2541): it follows SPF and DMARC alone. Both
 * Pass reads Pass; a record that is absent, failing, soft or neutral reads Fail; and a lookup the resolver did not answer on
 * either, with no hard failure beside it, reads TempError. DKIM never moves it, because a domain that signs nothing still
 * sends and is received.
 */
export type DomainOverall = 'Pass' | 'Fail' | 'TempError';

export function domainOverall(verdicts: MailDomainVerdicts): DomainOverall {
  const required = [verdicts.spf, verdicts.dmarc];
  if (required.every((verdict) => verdict === 'Pass')) return 'Pass';
  return required.some((verdict) => verdict !== 'Pass' && verdict !== 'TempError') ? 'Fail' : 'TempError';
}

/** 🔒 No DKIM key was found at any selector tried. That is a domain not set up for DKIM, never a failure. */
export function dkimNotSetUp(verdicts: MailDomainVerdicts): boolean {
  return verdicts.dkim === 'None';
}

/** SPF and DMARC read Pass; any other member, `None` included, is a record not yet seen to work. DKIM is not asked. */
export function verdictsPass(verdicts: MailDomainVerdicts | null | undefined): boolean {
  return verdicts !== null && verdicts !== undefined && domainOverall(verdicts) === 'Pass';
}

/**
 * 🔒 Each step's done test (decisions 1771, 2541). The records are published
 * when a real domain is current and its newest stored check passed SPF and
 * DMARC (decision 13291; DKIM is not required); the host is reached when one mailbox's Inbox carries the host's
 * UIDVALIDITY; the shared mailboxes are made when a Department and a Module
 * mailbox both exist; sending passed when the newest test send was accepted.
 * A key the answer lacks reads as not done.
 */
export function mailSetupSteps(view: Partial<MailSetupStepsView>): Record<MailSetupStep, MailSetupStepFacts> {
  const domain = Boolean(view.domain);
  const host = (view.hostReached ?? 0) > 0;
  const shared = (view.departmentMailboxes ?? 0) > 0 && (view.moduleMailboxes ?? 0) > 0;
  return {
    domain: { done: domain && verdictsPass(view.verdicts), reachable: true },
    host: { done: host, reachable: true },
    sharedMailboxes: { done: shared, reachable: host },
    sending: { done: view.testSend?.status === 'Accepted', reachable: host },
  };
}
